import { useState, useEffect, useMemo, useCallback } from "react";
import api from "../lib/api";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { MapPin, Settings2, Eye, EyeOff, Download, GripVertical } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#F43F5E", "#8B5CF6", "#14B8A6", "#F59E0B", "#64748B"];

const RANGE_STORAGE = "salonflow_reports_layout_v1";

// Date helpers
const toIso = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const day = x.getDay(); x.setDate(x.getDate() - day); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);

const RANGE_PRESETS = [
  { id: "7d", label: "Last 7 days", range: () => ({ start: toIso(addDays(today(), -6)), end: toIso(today()) }) },
  { id: "30d", label: "Last 30 days", range: () => ({ start: toIso(addDays(today(), -29)), end: toIso(today()) }) },
  { id: "90d", label: "Last 90 days", range: () => ({ start: toIso(addDays(today(), -89)), end: toIso(today()) }) },
  { id: "mtd", label: "Month to date", range: () => ({ start: toIso(startOfMonth(today())), end: toIso(today()) }) },
  { id: "wtd", label: "Week to date", range: () => ({ start: toIso(startOfWeek(today())), end: toIso(today()) }) },
  { id: "ytd", label: "Year to date", range: () => ({ start: toIso(startOfYear(today())), end: toIso(today()) }) },
  { id: "all", label: "All time", range: () => ({ start: "", end: "" }) },
];

const ALL_MODULES = [
  { id: "kpis", label: "KPI cards (Revenue / Tips / Tickets / Avg)" },
  { id: "trend", label: "Revenue trend chart" },
  { id: "breakdown", label: "Revenue breakdown (Services/Retail/Tips)" },
  { id: "daily", label: "Daily revenue bar chart" },
  { id: "providers", label: "Provider performance table" },
];

const DEFAULT_LAYOUT = {
  presetId: "30d",
  start: "",
  end: "",
  visible: { kpis: true, trend: true, breakdown: true, daily: false, providers: true },
  order: ["kpis", "trend", "breakdown", "daily", "providers"],
};

function loadLayout() {
  try {
    const raw = localStorage.getItem(RANGE_STORAGE);
    if (!raw) return DEFAULT_LAYOUT;
    const v = JSON.parse(raw);
    return { ...DEFAULT_LAYOUT, ...v, visible: { ...DEFAULT_LAYOUT.visible, ...(v.visible || {}) } };
  } catch { return DEFAULT_LAYOUT; }
}

export default function ReportsPage() {
  const [revenue, setRevenue] = useState([]);
  const [providers, setProviders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(loadLayout);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [prevRevenue, setPrevRevenue] = useState([]);

  // Persist layout on change
  useEffect(() => { localStorage.setItem(RANGE_STORAGE, JSON.stringify(layout)); }, [layout]);

  // Derive active range (preset or custom)
  const activeRange = useMemo(() => {
    if (layout.presetId === "custom") return { start: layout.start, end: layout.end };
    const p = RANGE_PRESETS.find(r => r.id === layout.presetId) || RANGE_PRESETS[1];
    return p.range();
  }, [layout.presetId, layout.start, layout.end]);

  const fetchAll = useCallback(async () => {
    try {
      const qs = [];
      if (selectedLocation && selectedLocation !== "all") qs.push(`location_id=${selectedLocation}`);
      if (activeRange.start) qs.push(`start=${activeRange.start}`);
      if (activeRange.end) qs.push(`end=${activeRange.end}`);
      const q = qs.length ? `?${qs.join("&")}` : "";
      const locQ = selectedLocation && selectedLocation !== "all" ? `?location_id=${selectedLocation}` : "";
      const fetches = [
        api.get("/locations"),
        api.get(`/dashboard/stats${locQ}`),
        api.get(`/reports/revenue${q}`),
        api.get(`/reports/providers${q}`),
      ];
      // Compare-to-previous-period
      let prevQs = "";
      if (compareMode && activeRange.start && activeRange.end) {
        const s = new Date(activeRange.start);
        const e = new Date(activeRange.end);
        const ms = e.getTime() - s.getTime();
        const prevEnd = new Date(s.getTime() - 24 * 60 * 60 * 1000);
        const prevStart = new Date(prevEnd.getTime() - ms);
        const pQs = [];
        if (selectedLocation && selectedLocation !== "all") pQs.push(`location_id=${selectedLocation}`);
        pQs.push(`start=${toIso(prevStart)}`);
        pQs.push(`end=${toIso(prevEnd)}`);
        prevQs = `?${pQs.join("&")}`;
        fetches.push(api.get(`/reports/revenue${prevQs}`));
      }
      const results = await Promise.all(fetches);
      const [locRes, statsRes, revRes, provRes, prevRes] = results;
      setLocations(locRes.data);
      setStats(statsRes.data);
      setRevenue(revRes.data);
      setProviders(provRes.data);
      setPrevRevenue(prevRes ? prevRes.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedLocation, activeRange.start, activeRange.end, compareMode]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pickPreset = (id) => {
    if (id === "custom") {
      const r = RANGE_PRESETS.find(r => r.id === layout.presetId)?.range() || { start: "", end: "" };
      setLayout(l => ({ ...l, presetId: "custom", start: l.start || r.start, end: l.end || r.end }));
    } else {
      setLayout(l => ({ ...l, presetId: id }));
    }
  };

  const toggleModule = (id) => setLayout(l => ({ ...l, visible: { ...l.visible, [id]: !l.visible[id] } }));

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setLayout(l => {
      const order = [...l.order];
      const from = order.indexOf(dragId);
      const to = order.indexOf(targetId);
      order.splice(from, 1);
      order.splice(to, 0, dragId);
      return { ...l, order };
    });
    setDragId(null);
  };

  const exportCsv = () => {
    if (!revenue.length) { toast.error("No data to export"); return; }
    const cols = ["date", "revenue", "transactions", "services", "retail", "tips"];
    const rows = [cols.join(",")].concat(revenue.map(r => cols.map(c => r[c] || 0).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `revenue-${activeRange.start || "all"}_to_${activeRange.end || "today"}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Revenue CSV downloaded");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalRev = revenue.reduce((s, d) => s + d.revenue, 0);
  const totalTips = revenue.reduce((s, d) => s + (d.tips || 0), 0);
  const totalTxns = revenue.reduce((s, d) => s + d.transactions, 0);
  const totalServices = revenue.reduce((s, d) => s + (d.services || 0), 0);
  const totalRetail = revenue.reduce((s, d) => s + (d.retail || 0), 0);
  const avgTicket = totalTxns ? totalRev / totalTxns : 0;

  // Previous period totals for deltas
  const prevTotalRev = prevRevenue.reduce((s, d) => s + d.revenue, 0);
  const prevTotalServices = prevRevenue.reduce((s, d) => s + (d.services || 0), 0);
  const prevTotalRetail = prevRevenue.reduce((s, d) => s + (d.retail || 0), 0);
  const prevTotalTxns = prevRevenue.reduce((s, d) => s + d.transactions, 0);
  const prevAvgTicket = prevTotalTxns ? prevTotalRev / prevTotalTxns : 0;

  const delta = (cur, prev) => {
    if (!compareMode || !prevRevenue.length) return null;
    if (prev === 0) return cur > 0 ? { pct: 100, up: true } : null;
    const pct = ((cur - prev) / prev) * 100;
    return { pct: Math.abs(pct), up: pct >= 0 };
  };

  // Build merged trend data (current vs previous period overlay)
  const mergedTrend = (() => {
    if (!compareMode || !prevRevenue.length) return revenue;
    const len = Math.max(revenue.length, prevRevenue.length);
    const arr = [];
    for (let i = 0; i < len; i++) {
      arr.push({
        date: revenue[i]?.date || prevRevenue[i]?.date || `#${i + 1}`,
        revenue: revenue[i]?.revenue ?? null,
        prev_revenue: prevRevenue[i]?.revenue ?? null,
      });
    }
    return arr;
  })();

  const activePreset = RANGE_PRESETS.find(r => r.id === layout.presetId);
  const rangeLabel = layout.presetId === "custom"
    ? `${layout.start || "…"} → ${layout.end || "…"}`
    : activePreset?.label;

  // Modules renderer (keyed by id, ordered by layout.order, filtered by visible)
  const moduleRenderers = {
    kpis: () => {
      const cards = [
        { label: "Revenue", value: `$${totalRev.toFixed(0)}`, sub: `${totalTxns} transactions`, d: delta(totalRev, prevTotalRev) },
        { label: "Services", value: `$${totalServices.toFixed(0)}`, d: delta(totalServices, prevTotalServices) },
        { label: "Retail", value: `$${totalRetail.toFixed(0)}`, d: delta(totalRetail, prevTotalRetail) },
        { label: "Avg Ticket", value: `$${avgTicket.toFixed(0)}`, sub: `$${totalTips.toFixed(0)} tips`, d: delta(avgTicket, prevAvgTicket) },
      ];
      return (
        <div key="kpis" className="col-span-full grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="report-kpis">
          {cards.map(card => (
            <div key={card.label} className="bg-white border border-slate-100 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">{card.label}</span>
                {card.d && (
                  <span className={`flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${card.d.up ? "text-emerald-600" : "text-rose-500"}`} data-testid={`delta-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
                    {card.d.up ? "↑" : "↓"} {card.d.pct.toFixed(0)}%
                  </span>
                )}
              </div>
              <p className="font-['Outfit'] text-3xl font-semibold text-slate-900 tabular-nums">{card.value}</p>
              {card.sub && <p className="text-xs text-slate-400 mt-1 font-['Manrope']">{card.sub}</p>}
            </div>
          ))}
        </div>
      );
    },
    trend: () => (
      <div key="trend" className="bg-white border border-slate-100 rounded-2xl p-6" data-testid="revenue-trend-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 tracking-tight">Revenue Trend</h3>
          {compareMode && prevRevenue.length > 0 && (
            <div className="flex items-center gap-3 text-[10px] font-semibold font-['Manrope']">
              <div className="flex items-center gap-1"><div className="w-3 h-0.5 bg-rose-500" /><span className="text-slate-600 uppercase tracking-widest">Current</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-0.5 bg-slate-300" /><span className="text-slate-400 uppercase tracking-widest">Previous</span></div>
            </div>
          )}
        </div>
        <div className="h-[260px]">
          {revenue.length === 0 ? <EmptyState label="No revenue in this range" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mergedTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v && v.length >= 10 ? new Date(v + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : v} axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `$${v}`} axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} />
                <Tooltip formatter={(v) => v == null ? "—" : `$${v.toFixed(2)}`} contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }} />
                {compareMode && prevRevenue.length > 0 && (
                  <Line type="monotone" dataKey="prev_revenue" name="Previous" stroke="#CBD5E1" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                )}
                <Line type="monotone" dataKey="revenue" name="Current" stroke="#F43F5E" strokeWidth={2.5} dot={{ fill: '#F43F5E', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    ),
    breakdown: () => (
      <div key="breakdown" className="bg-white border border-slate-100 rounded-2xl p-6" data-testid="revenue-breakdown">
        <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-4 tracking-tight">Revenue Breakdown</h3>
        <div className="h-[260px] flex items-center">
          {totalRev === 0 ? <EmptyState label="No revenue to break down" /> : (
            <>
              <ResponsiveContainer width="55%" height="100%">
                <PieChart>
                  <Pie data={[
                    { name: "Services", value: totalServices },
                    { name: "Retail", value: totalRetail },
                    { name: "Tips", value: totalTips },
                  ]} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                    {COLORS.slice(0, 3).map((c, i) => <Cell key={i} fill={c} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `$${v.toFixed(0)}`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 pl-4">
                {[
                  { name: "Services", value: totalServices, color: COLORS[0] },
                  { name: "Retail", value: totalRetail, color: COLORS[1] },
                  { name: "Tips", value: totalTips, color: COLORS[2] },
                ].map(item => (
                  <div key={item.name}>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-slate-500 font-['Manrope']">{item.name}</span>
                    </div>
                    <p className="font-['Outfit'] font-semibold text-slate-900 tabular-nums ml-4">${item.value.toFixed(0)}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    ),
    daily: () => (
      <div key="daily" className="bg-white border border-slate-100 rounded-2xl p-6 col-span-full" data-testid="daily-revenue-bar">
        <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-4 tracking-tight">Daily Revenue</h3>
        <div className="h-[260px]">
          {revenue.length === 0 ? <EmptyState label="No daily data" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `$${v}`} axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} />
                <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }} />
                <Bar dataKey="revenue" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    ),
    providers: () => (
      <div key="providers" className="bg-white border border-slate-100 rounded-2xl overflow-hidden col-span-full" data-testid="provider-performance">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 tracking-tight">Provider Performance</h3>
        </div>
        {providers.length === 0 ? (
          <div className="p-10"><EmptyState label="No providers in this range" /></div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100">
                <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Provider</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Role</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Appts</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Completed</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Revenue</th>
                <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Avg Ticket</th>
              </tr>
            </thead>
            <tbody>
              {providers.map(p => (
                <tr key={p.user_id} className="border-b border-slate-100 hover:bg-slate-50/40 transition-colors">
                  <td className="px-6 py-3.5 text-sm font-semibold text-slate-900">{p.name}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-500 capitalize">{p.role?.replace('_', ' ')}</td>
                  <td className="px-4 py-3.5 text-sm text-right text-slate-900 tabular-nums">{p.total_appointments}</td>
                  <td className="px-4 py-3.5 text-sm text-right text-emerald-600 tabular-nums">{p.completed}</td>
                  <td className="px-4 py-3.5 text-sm text-right font-semibold text-slate-900 tabular-nums">${p.revenue.toFixed(0)}</td>
                  <td className="px-6 py-3.5 text-sm text-right text-slate-900 tabular-nums">${p.avg_ticket.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    ),
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="reports-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Reports</h1>
          <p className="text-slate-500 text-sm mt-1 font-['Manrope']">{rangeLabel} · {providers.length} providers · {totalTxns} transactions</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Range picker */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1" data-testid="range-preset-group">
            {RANGE_PRESETS.slice(0, 4).map(p => (
              <button key={p.id} onClick={() => pickPreset(p.id)}
                data-testid={`range-${p.id}`}
                className={`text-xs font-medium font-['Manrope'] px-3 h-8 rounded-full transition-all ${
                  layout.presetId === p.id ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                }`}>{p.label.replace("Last ", "")}</button>
            ))}
            <Select value={["mtd","wtd","ytd","all","custom"].includes(layout.presetId) ? layout.presetId : "more"} onValueChange={pickPreset}>
              <SelectTrigger className={`w-auto h-8 px-3 border-0 bg-transparent text-xs font-medium font-['Manrope'] rounded-full ${
                ["mtd","wtd","ytd","all","custom"].includes(layout.presetId) ? "bg-slate-900 text-white" : "text-slate-600"
              }`} data-testid="range-more-trigger">
                <SelectValue placeholder="More" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="wtd">Week to date</SelectItem>
                <SelectItem value="mtd">Month to date</SelectItem>
                <SelectItem value="ytd">Year to date</SelectItem>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="custom">Custom range…</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom date inputs */}
          {layout.presetId === "custom" && (
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2 h-10" data-testid="custom-range-inputs">
              <input type="date" value={layout.start} max={layout.end || undefined}
                onChange={e => setLayout(l => ({ ...l, start: e.target.value }))}
                data-testid="custom-start" className="text-xs px-2 h-8 rounded-md focus:outline-none font-['Manrope']" />
              <span className="text-slate-300 text-xs">→</span>
              <input type="date" value={layout.end} min={layout.start || undefined}
                onChange={e => setLayout(l => ({ ...l, end: e.target.value }))}
                data-testid="custom-end" className="text-xs px-2 h-8 rounded-md focus:outline-none font-['Manrope']" />
            </div>
          )}

          {/* Location */}
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger data-testid="report-location-select" className="w-[180px] h-10 border-slate-200 bg-white rounded-full text-sm">
              <MapPin className="w-3.5 h-3.5 mr-1 text-slate-400" />
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={exportCsv} data-testid="export-revenue-btn" className="border-slate-200 rounded-full h-10 px-4 text-sm">
            <Download className="w-3.5 h-3.5 mr-1.5" />Export
          </Button>
          <button onClick={() => setCompareMode(v => !v)} data-testid="compare-toggle"
            className={`h-10 px-4 rounded-full text-sm font-medium font-['Manrope'] border transition-all ${
              compareMode
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}>
            {compareMode ? "Comparing ✓" : "Compare"}
          </button>
          <Button onClick={() => setCustomizeOpen(true)} data-testid="customize-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-5 text-sm">
            <Settings2 className="w-3.5 h-3.5 mr-1.5" />Customize
          </Button>
        </div>
      </div>

      {/* Modules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="report-modules">
        {layout.order.filter(id => layout.visible[id]).map(id => moduleRenderers[id]?.())}
      </div>

      {/* Customize Dialog */}
      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent className="max-w-md" data-testid="customize-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl tracking-tight">Customize Reports</DialogTitle>
            <p className="text-xs text-slate-500 font-['Manrope'] mt-0.5">Drag to reorder. Click the eye to show/hide a module.</p>
          </DialogHeader>
          <div className="space-y-1.5 mt-4" data-testid="module-list">
            {layout.order.map(id => {
              const mod = ALL_MODULES.find(m => m.id === id);
              if (!mod) return null;
              const on = layout.visible[id];
              return (
                <div key={id}
                  draggable
                  onDragStart={() => onDragStart(id)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(id)}
                  data-testid={`module-row-${id}`}
                  className={`flex items-center gap-2 p-2.5 rounded-xl border transition-all cursor-grab active:cursor-grabbing ${
                    dragId === id ? "opacity-40" : "border-slate-100 hover:bg-slate-50"
                  }`}>
                  <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  <p className={`flex-1 text-sm font-['Manrope'] truncate ${on ? "text-slate-900 font-medium" : "text-slate-400 line-through"}`}>{mod.label}</p>
                  <button onClick={() => toggleModule(id)}
                    data-testid={`toggle-${id}`}
                    className={`p-2 rounded-lg transition-colors ${on ? "text-slate-700 hover:bg-white" : "text-slate-300 hover:text-slate-600"}`}>
                    {on ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-slate-100">
            <Button variant="ghost" onClick={() => setLayout(DEFAULT_LAYOUT)} data-testid="reset-layout"
              className="text-xs text-slate-500 hover:text-slate-900 h-9">
              Reset to default
            </Button>
            <Button onClick={() => setCustomizeOpen(false)} className="rounded-full h-9 px-5 text-sm bg-slate-900 text-white hover:bg-slate-800">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="w-full h-full flex items-center justify-center text-sm text-slate-400 font-['Manrope']">{label}</div>
  );
}
